import { normalizeStripeEnvValue } from "@/utils/stripe/price-env";
import {
  SILVER_MONTHLY_PRICE_ID,
  GOLD_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  SILVER_YEARLY_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  DIAMOND_PRODUCTION_PRICE_IDS,
  ADDON_EV_SNIPER_MONTHLY_PRICE_ID,
  ADDON_EV_SNIPER_MONTHLY_EXPERIMENT_PRICE_ID,
  ADDON_VIP_DISCORD_MONTHLY_PRICE_ID,
  ADDON_VIP_DISCORD_YEARLY_PRICE_ID,
} from "@/lib/billing/tier-price-ids";

export type SubscriptionSlug =
  | "ev-plan"
  | "whale-watch"
  | "ev-sniper"
  | "silver"
  | "gold"
  | "diamond"
  | "vip-discord";

function collectKnownPriceIds(values: Array<string | undefined>): string[] {
  const unique = new Set<string>();

  for (const value of values) {
    const normalized = normalizeStripeEnvValue(value);
    if (!normalized) continue;
    unique.add(normalized);
  }

  return Array.from(unique);
}

const EV_PLAN_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_EV_OPTIMIZER_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_OPTIMIZER_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EV_PLAN_MONTHLY_EXPERIMENT_PRICE_ID,
  "price_1SXocCGrgdPJmrXHYzrnVO6G",
  "price_1SXobaGrgdPJmrXHRNiMDPNj",
  "price_1RU9xSGrgdPJmrXHskT7e5i1",
  "price_1RU9yzGrgdPJmrXHjJCU60Ir",
  "price_1T957LGrgdPJmrXH8P3kQfHH",
  "price_1T1dyWGrgdPJmrXH0mOE6dXC",
  "price_1SkHXgGrgdPJmrXH1EK1cFqO",
  "price_1ScqLIGrgdPJmrXHSGJenfpZ",
  "price_1T2IJKGrgdPJmrXHPIQ1NKsS",
]);

const WHALE_WATCH_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_YEARLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_WHALE_WATCH_MONTHLY_EXPERIMENT_PRICE_ID,
  "price_1SslQ2GrgdPJmrXHZnffizhY",
  "price_1SslRYGrgdPJmrXHXvjmqKI8",
  "price_1T6aY1GrgdPJmrXHzJQkoAXK",
  "price_1TDnVdGrgdPJmrXHJO27xhpq",
  "price_1SuHHFGrgdPJmrXH8SKddpPd",
  "price_1StuKkGrgdPJmrXHrkWEDDNc",
  "price_1StuCFGrgdPJmrXHsHypVu1S",
  "price_1Sttg5GrgdPJmrXH246fKu3w",
]);

const LEGACY_EPICK_PRICE_IDS = collectKnownPriceIds([
  process.env.NEXT_PUBLIC_STRIPE_EPICK_TRADER_MONTHLY_PRICE_ID,
  process.env.NEXT_PUBLIC_STRIPE_EPICK_TRADER_YEARLY_PRICE_ID,
  "price_1SIrRrGrgdPJmrXHAMUNjPYw",
  "price_1SIrS7GrgdPJmrXHpnphDe0u",
  "price_1SXRZOGrgdPJmrXHiDzt6Hy2",
]);

const SILVER_PRICE_IDS = collectKnownPriceIds([
  SILVER_MONTHLY_PRICE_ID,
  SILVER_YEARLY_PRICE_ID,
]);
const GOLD_PRICE_IDS = collectKnownPriceIds([
  GOLD_MONTHLY_PRICE_ID,
  GOLD_YEARLY_PRICE_ID,
]);
const DIAMOND_PRICE_IDS = collectKnownPriceIds([
  DIAMOND_MONTHLY_PRICE_ID,
  DIAMOND_MONTHLY_EXPERIMENT_PRICE_ID,
  DIAMOND_YEARLY_PRICE_ID,
  ...DIAMOND_PRODUCTION_PRICE_IDS,
]);
const EV_SNIPER_PRICE_IDS = collectKnownPriceIds([
  ADDON_EV_SNIPER_MONTHLY_PRICE_ID,
  ADDON_EV_SNIPER_MONTHLY_EXPERIMENT_PRICE_ID,
]);
const VIP_DISCORD_PRICE_IDS = collectKnownPriceIds([
  ADDON_VIP_DISCORD_MONTHLY_PRICE_ID,
  ADDON_VIP_DISCORD_YEARLY_PRICE_ID,
]);

const SUBSCRIPTION_SLUG_PRICE_IDS: Record<SubscriptionSlug, string[]> = {
  "ev-plan": [...EV_PLAN_PRICE_IDS, ...LEGACY_EPICK_PRICE_IDS],
  "whale-watch": WHALE_WATCH_PRICE_IDS,
  "ev-sniper": EV_SNIPER_PRICE_IDS,
  silver: SILVER_PRICE_IDS,
  gold: GOLD_PRICE_IDS,
  diamond: DIAMOND_PRICE_IDS,
  "vip-discord": VIP_DISCORD_PRICE_IDS,
};

const PRICE_ID_TO_SUBSCRIPTION_SLUG = new Map<string, SubscriptionSlug>(
  Object.entries(SUBSCRIPTION_SLUG_PRICE_IDS).flatMap(([slug, priceIds]) =>
    priceIds.map((priceId) => [priceId, slug as SubscriptionSlug] as const),
  ),
);

export function getCanonicalSubscriptionSlugForPriceId(
  priceId: string | null | undefined,
): SubscriptionSlug | null {
  if (!priceId) return null;
  return PRICE_ID_TO_SUBSCRIPTION_SLUG.get(priceId) ?? null;
}

export function getAllKnownSubscriptionPriceIds(): string[] {
  return Array.from(PRICE_ID_TO_SUBSCRIPTION_SLUG.keys());
}
